import React from 'react'
import { isNull } from 'lodash'
import {
  SortDirectionEnum,
  TestsFull_tests_pagination,
  GetDefaultPrice_getDefaultPrice_defaultPrices,
  SubLocation,
  SublocationName,
} from '@modmd/data'
import { DEVICE } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import Datalist, { DataListProps } from 'components/Datalist'
import Pagination from 'components/Pagination'
import Card from 'components/Card'
import Button from 'components/Button'
import Icon from 'components/Icon'
import { Box } from 'components/Layout'

interface ColumnLabels {
  patient?: string | null
  barcode?: string | null
  testCategory?: string | null
  result?: string | null
  appointmentDate?: string | null
  testedAt?: string | null
  resultProvisionedAt?: string | null
  labName?: string | null
  paymentStatus?: string | null
  paymentMethod?: string | null
}

interface Props {
  tableHeader?: React.ReactElement
  tableFooter?: React.ReactElement
  prices: GetDefaultPrice_getDefaultPrice_defaultPrices[]
  pagination?: TestsFull_tests_pagination
  sorter?: DataListProps['sorter']
  requestDebounceInMs?: number
  isLoading: boolean
  pageLength?: number
  columnLabels?: ColumnLabels
  onSortChange?: (sortBy: string, direction: SortDirectionEnum) => void
  onPageChange?: (page: number) => void
  onRowClick?: (data: GetDefaultPrice_getDefaultPrice_defaultPrices) => void
  handleDelete: (
    item: GetDefaultPrice_getDefaultPrice_defaultPrices,
    e: React.MouseEvent<HTMLElement, MouseEvent>
  ) => void
  handleEdit: (
    item: GetDefaultPrice_getDefaultPrice_defaultPrices,
    e: React.MouseEvent<HTMLElement, MouseEvent>
  ) => void
}

const defaultColumnLabels = {
  name: 'Name',
  location: 'Location',
  price: 'Price',
  minQuantity: 'Min. Quantity',
  actions: 'Actions',
  sublocation: 'Rate type',
}

const PAGE_LENGTH = 10

export const DefaultPriceDatalist: React.VFC<Props> = ({
  tableHeader,
  tableFooter,
  prices,
  sorter,
  pagination,
  isLoading,
  columnLabels: providedColumnLabels,
  onSortChange,
  onPageChange,
  onRowClick,
  pageLength = PAGE_LENGTH,
  handleDelete,
  handleEdit,
}) => {
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const columnLabels = { ...defaultColumnLabels, ...(providedColumnLabels || {}) }
  const numOfColumns = Object.values(columnLabels).filter((label) => label !== null).length
  const rowGridTemplateColumns = '2fr 2fr 1fr 1fr 1fr 1fr'

  return (
    <>
      <Card>
        <Datalist
          header={tableHeader}
          isLoading={isLoading}
          skeletonProps={{
            columns: numOfColumns,
            rows: pageLength,
          }}
          data={prices}
          sorter={sorter}
          onSortChange={onSortChange}
          shouldRenderTableHeader={isDesktop}
          showActionIndicators
          renderTableHeader={() => (
            <Datalist.Row gridTemplateColumns={rowGridTemplateColumns}>
              {!isNull(columnLabels.patient) ? (
                <Datalist.HeaderCell label={columnLabels.name} />
              ) : null}
              {!isNull(columnLabels.barcode) ? (
                <Datalist.HeaderCell label={columnLabels.location} />
              ) : null}
              {!isNull(columnLabels.testCategory) ? (
                <Datalist.HeaderCell label={columnLabels.price} />
              ) : null}
              {!isNull(columnLabels.result) ? (
                <Datalist.HeaderCell label={columnLabels.minQuantity} />
              ) : null}
              {!isNull(columnLabels.sublocation) ? (
                <Datalist.HeaderCell label={columnLabels.sublocation} />
              ) : null}
              {!isNull(columnLabels.appointmentDate) ? (
                <Datalist.HeaderCell label={columnLabels.actions} />
              ) : null}
            </Datalist.Row>
          )}
          renderTableRow={(data) => {
            const { Product, TestLocation, price, minQuantity, sublocation } = data
            return (
              <Datalist.Row
                gridTemplateColumns={{
                  _: 'repeat(2, 1fr)',
                  DESKTOP: rowGridTemplateColumns,
                }}
                onClick={
                  onRowClick
                    ? () => {
                        onRowClick(data)
                      }
                    : undefined
                }
                {...(!isDesktop ? { alignItems: 'start' } : {})}
              >
                {!isNull(columnLabels.name) ? (
                  <Datalist.Cell label={columnLabels.name}>
                    {Product && <>{Product.name}</>}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.location) ? (
                  <Datalist.Cell label={columnLabels.location}>
                    {TestLocation && <>{TestLocation.name}</>}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.price) ? (
                  <Datalist.Cell label={columnLabels.price}>$ {price}</Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.minQuantity) ? (
                  <Datalist.Cell label={columnLabels.minQuantity}>{minQuantity}</Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.sublocation) ? (
                  <Datalist.Cell label={columnLabels.sublocation}>
                    {SublocationName[sublocation as SubLocation]}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.actions) ? (
                  <Datalist.Cell label={columnLabels.actions}>
                    <Box flexDirection="row">
                      <Button
                        disabled={false}
                        appearance="ghost"
                        colorVariant="info"
                        size="small"
                        onClick={(e) => {
                          handleEdit(data, e)
                        }}
                      >
                        <Icon.Edit />
                      </Button>
                      <Button
                        disabled={false}
                        appearance="ghost"
                        colorVariant="danger"
                        size="small"
                        onClick={(e) => {
                          handleDelete(data, e)
                        }}
                      >
                        <Icon.Trash />
                      </Button>
                    </Box>
                  </Datalist.Cell>
                ) : null}
              </Datalist.Row>
            )
          }}
          footer={tableFooter}
        />
      </Card>
      {pagination && pagination.total > pagination.length && onPageChange && (
        <Pagination.Wrapper>
          <Pagination
            firstPage={1}
            lastPage={Math.ceil(pagination.total / pagination.length) || 1}
            currentPage={Math.ceil(pagination.from / pagination.length) + 1}
            onPageChange={onPageChange}
          />
        </Pagination.Wrapper>
      )}
    </>
  )
}
